import { useEffect, useRef, useState } from "react";
import ChannelConnection from "../../js/connection/ChannelConnection";
import ConnectionData from "../../js/connection/ConnectionData";

/**
 * While a tile is pinned for annotation, shows the other participants
 * in a vertical sidebar so they remain visible.
 */
export default function FocusFilmstrip({ connection }: { connection: ChannelConnection }) {
    const [others, setOthers] = useState<ConnectionData[]>([]);
    const [focusedId, setFocusedId] = useState<string | null>(null);
    const thumbRefs = useRef<Record<string, HTMLDivElement | null>>({});

    useEffect(() => {
        function sync() {
            const id = connection.sfuConnection.getFocusedViewId();
            setFocusedId(id);
            setOthers(id ? connection.sfuConnection.getUnfocusedConnections() : []);
        }

        connection.events.addListener("onviewfocus", sync);
        connection.events.addListener("onconnectionopen", sync);
        connection.events.addListener("onconnectionclose", sync);
        connection.events.addListener("onvideolayout", sync);
        sync();

        return () => {
            connection.events.removeListener("onviewfocus", sync);
            connection.events.removeListener("onconnectionopen", sync);
            connection.events.removeListener("onconnectionclose", sync);
            connection.events.removeListener("onvideolayout", sync);
        };
    }, [connection]);

    useEffect(() => {
        for (const conn of others) {
            const container = thumbRefs.current[conn.id];
            if (!container || !conn.view) continue;

            container.innerHTML = "";
            const sourceVideo = conn.view.querySelector("video") as HTMLVideoElement | null;
            if (!sourceVideo?.srcObject) continue;

            const video = document.createElement("video");
            video.srcObject = sourceVideo.srcObject;
            video.autoplay = true;
            video.muted = true;
            video.playsInline = true;
            video.style.cssText = [
                "width:100%",
                "height:100%",
                "object-fit:cover",
                "display:block",
                `transform:${conn.isLocal && !conn.isScreen ? "scaleX(-1)" : "none"}`,
            ].join(";");
            container.appendChild(video);
            video.play().catch(() => {});
        }
    }, [others]);

    if (!focusedId || others.length === 0) {
        return null;
    }

    return (
        <aside className="focus-filmstrip" aria-label="Other participants">
            <div className="focus-filmstrip-scroll">
                {others.map((conn) => (
                    <button
                        key={conn.id}
                        type="button"
                        className="focus-filmstrip-tile"
                        title={`Pin ${conn.userAlias || "participant"}`}
                        onClick={() => connection.focusView(conn.id)}
                    >
                        <div
                            className="focus-filmstrip-video"
                            ref={(el) => {
                                thumbRefs.current[conn.id] = el;
                            }}
                            style={{ background: conn.color || "#1e293b" }}
                        />
                        <span className="focus-filmstrip-name">
                            {conn.isLocal ? "You" : conn.userAlias || "Participant"}
                        </span>
                        {conn.isScreen && <span className="focus-filmstrip-badge">Screen</span>}
                    </button>
                ))}
            </div>

            <style>{`
                .focus-filmstrip {
                    width: 132px;
                    flex-shrink: 0;
                    display: flex;
                    flex-direction: column;
                    min-height: 0;
                    background: var(--hx-card, #fff);
                    border: 1px solid var(--hx-line, #e2e8f0);
                    border-radius: var(--hx-radius, 16px);
                    box-shadow: var(--hx-shadow-sm, 0 4px 16px rgba(15, 23, 42, 0.06));
                    overflow: hidden;
                }

                .focus-filmstrip-scroll {
                    flex: 1;
                    min-height: 0;
                    overflow-y: auto;
                    overflow-x: hidden;
                    padding: 0.55rem;
                    display: flex;
                    flex-direction: column;
                    gap: 0.55rem;
                }

                .focus-filmstrip-scroll::-webkit-scrollbar {
                    display: block;
                    width: 4px;
                }

                .focus-filmstrip-scroll::-webkit-scrollbar-thumb {
                    background: var(--hx-line, #e2e8f0);
                    border-radius: 4px;
                }

                .focus-filmstrip-tile {
                    position: relative;
                    display: block;
                    width: 100%;
                    padding: 0;
                    border: 1.5px solid var(--hx-line, #e2e8f0);
                    border-radius: 12px;
                    overflow: hidden;
                    cursor: pointer;
                    background: #0f172a;
                    aspect-ratio: 3 / 4;
                    flex-shrink: 0;
                    transition: border-color 0.15s, box-shadow 0.15s, transform 0.1s;
                    font-family: inherit;
                }

                .focus-filmstrip-tile:hover {
                    border-color: var(--hx-blue, #2563eb);
                    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.15);
                    transform: translateY(-1px);
                }

                .focus-filmstrip-video {
                    position: absolute;
                    inset: 0;
                    width: 100%;
                    height: 100%;
                }

                .focus-filmstrip-name {
                    position: absolute;
                    left: 6px;
                    right: 6px;
                    bottom: 6px;
                    padding: 0.2rem 0.4rem;
                    border-radius: 6px;
                    background: rgba(15, 23, 42, 0.72);
                    color: #f8fafc;
                    font-size: 0.68rem;
                    font-weight: 650;
                    text-align: left;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    z-index: 1;
                }

                .focus-filmstrip-badge {
                    position: absolute;
                    top: 6px;
                    left: 6px;
                    z-index: 1;
                    font-size: 0.6rem;
                    font-weight: 700;
                    letter-spacing: 0.04em;
                    text-transform: uppercase;
                    color: #fff;
                    background: rgba(37, 99, 235, 0.9);
                    padding: 0.15rem 0.4rem;
                    border-radius: 999px;
                }

                @media (max-width: 768px) {
                    .focus-filmstrip {
                        width: auto;
                        height: 96px;
                        flex-direction: row;
                        border-radius: 12px;
                    }

                    .focus-filmstrip-scroll {
                        flex-direction: row;
                        overflow-x: auto;
                        overflow-y: hidden;
                        padding: 0.4rem;
                        gap: 0.4rem;
                    }

                    .focus-filmstrip-tile {
                        width: 78px;
                        height: 100%;
                        aspect-ratio: auto;
                        flex: 0 0 auto;
                    }
                }
            `}</style>
        </aside>
    );
}
